import { $, cityHooks, enterStudio, fade, guard, loadCity, startCity, studio, ui, wait } from './app';
import { prefetchCity } from './prefetch';
import { GAME } from './features';
import type { PanelId } from './studio/content';

// The entry: the studio is the front door. This file and app.ts are all a visitor downloads before the room; the city
// (city.ts, with its physics engine) loads in the background once the room is up, or straight away on a #drive link.

// buttons that belong to the studio or to both scenes (the city's own controls are in city.ts)
$('go-studio').addEventListener('click', () => { void enterStudio(); });
$('enter-studio').addEventListener('click', () => { void enterStudio(); });
$('skip-to-studio').addEventListener('click', () => { void enterStudio(); });
$('world-contact').addEventListener('click', () => studio.panels.open('contact'));
$('studio-back').addEventListener('click', () => studio.exit());
$('studio-contact').addEventListener('click', () => studio.openPanel('contact'));
document.querySelectorAll<HTMLButtonElement>('[data-open]').forEach((b) => b.addEventListener('click', () => studio.openPanel(b.dataset.open as PanelId)));
(window as unknown as { __STUDIO__?: unknown }).__STUDIO__ = { info: () => studio.debugInfo(), focus: (id: string) => studio.debugFocus(id), back: () => studio.panels.close(), exit: () => studio.exit(), enter: () => enterStudio() };

// the frame loop runs under the guard: a frame that throws is skipped, a paused guard (GPU reset, error burst) idles
let lastFrame = performance.now();
function loop(now: number) {
  requestAnimationFrame(loop);
  const dt = (now - lastFrame) / 1000;
  lastFrame = now;
  if (guard.paused) return;
  guard.frame(now, dt, () => { if (studio.active) studio.update(Math.min(dt, 0.1)); else cityHooks.frame(now); });
}

// a visitor saving data, or on a slow link, downloads the city only when they drive (as prefetch.ts does for its files)
const net = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
const slowLink = Boolean(net?.saveData) || /(^|-)(2g|3g)$/.test(net?.effectiveType ?? '');

// A #drive link starts in the city, and so do automated test browsers unless they ask for #studio (index.html makes
// the same choice for the landing still). While the game is not live (features.ts) every visit is the studio's.
if (!GAME && location.hash === '#drive') history.replaceState(null, '', location.pathname + location.search + '#studio');
if (GAME && (location.hash === '#drive' || (navigator.webdriver && location.hash !== '#studio'))) startCity();
else {
  const later = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 600));
  // the landing's name reveal takes ~1.8 s: a room already in the cache waits for it, then the landing dissolves into
  // the studio (its styles stay through the 1.2 s exit)
  const intro = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1800;
  void Promise.all([studio.load(), wait(intro)]).then(() => enterStudio()).catch((error: unknown) => { console.error(error); fade.classList.remove('on'); ui.transitioning = false; guard.fail('studio', error); }).finally(() => {
    window.setTimeout(() => document.documentElement.classList.remove('studio-first'), 1300);
    // once the room is up, the city's files (prefetch.ts) and then its code download behind it, so Drive is instant
    if (GAME) later(() => void prefetchCity((f) => studio.setCityProgress(f)).then(() => { if (!slowLink) void loadCity(); }), { timeout: 1500 });
    else studio.setCityProgress(0);   // the Drive button says Coming soon
  });
}
requestAnimationFrame(loop);
